import { attempt } from "@omni/utils/function.utils";
import { cn } from "@omni/utils/styles";
import { ChangeEvent, HTMLInputAutoCompleteAttribute, useRef } from "react";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import { mergeRefs } from "@/utils/react.utils";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";

type TextAreaFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    placeholder?: string;
    disabled?: boolean;
    maxLength?: number;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    minLength?: number;
    rows?: number;
    optionalIndication?: boolean;
    requiredIndication?: boolean;
};

const TextAreaField = <T extends FieldValues>({
    label,
    name,
    microCopy,
    required,
    placeholder,
    maxLength,
    autoComplete,
    minLength,
    disabled,
    rows = 3,
    optionalIndication,
    requiredIndication
}: TextAreaFieldProps<T>) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const { handleOnAutofill } = useAutofill<T>(name);

    const handleChange = (
        e: ChangeEvent<HTMLTextAreaElement>,
        onChangeCB: ControllerRenderProps<T, Path<T>>["onChange"]
    ) => {
        const { value = "" } = e.currentTarget;
        const parsedValue = value;

        const hasPassedMaxLength =
            maxLength &&
            maxLength > 0 &&
            parsedValue.toString().length > maxLength;
        if (hasPassedMaxLength) {
            e.preventDefault();
            return;
        }
        onChangeCB?.(e);

        const textarea = textareaRef.current;
        if (!textarea) return;

        const autofilled = attempt(() => textarea.matches(":autofill"), false);

        if (autofilled) {
            handleOnAutofill();
        }
    };

    return (
        <BaseField<T, Path<T>>
            label={label}
            name={name}
            microCopy={microCopy}
            required={required}
            optionalIndication={optionalIndication}
            requiredIndication={requiredIndication}
        >
            {({ field, fieldState }) => {
                const classes = cn(
                    fieldState.isInvalid && "is-invalid",
                    fieldState.isValid && "is-valid",
                    "form-control"
                );

                return (
                    <div className={cn("tw:relative")}>
                        <textarea
                            {...field}
                            ref={mergeRefs(textareaRef, field.ref)}
                            className={classes}
                            id={name}
                            placeholder={placeholder ? placeholder : ""}
                            aria-invalid={fieldState.isInvalid}
                            onChange={(e) => handleChange(e, field.onChange)}
                            disabled={disabled}
                            rows={rows}
                            autoComplete={autoComplete}
                        />
                        {maxLength && (
                            <small
                                className={cn(
                                    "tw:absolute tw:-bottom-6 tw:right-0"
                                )}
                            >{`${
                                field.value?.length || 0
                            }/${maxLength}`}</small>
                        )}
                        {minLength && !!field.value?.length && (
                            <small
                                className={cn(
                                    "tw:absolute tw:-bottom-6 tw:right-0"
                                )}
                            >
                                {field.value.length}
                            </small>
                        )}
                    </div>
                );
            }}
        </BaseField>
    );
};

export { TextAreaField };
